import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { AuthShell, Field, inputClass, btnClass, AuthLink } from "./AuthShell";

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState("loading");
  const token = params.get("token");
  const fired = useRef(false);
  useEffect(() => {
    if (!token) { setState("error"); return; }
    if (fired.current) return;  // guard against React StrictMode double-invoke consuming the token twice
    fired.current = true;
    api.get(`/auth/verify-email?token=${token}`)
      .then(() => setState("ok")).catch(() => setState("error"));
  }, [token]);
  return (
    <AuthShell title="Email verification" testid="verify-email-page">
      <div className="text-center space-y-4">
        {state === "loading" && <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto" />}
        {state === "ok" && (<>
          <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
          <p className="text-slate-700" data-testid="verify-success">Your email is verified. You can now sign in.</p>
          <AuthLink to="/login">Go to sign in</AuthLink>
        </>)}
        {state === "error" && (<>
          <XCircle className="w-14 h-14 text-rose-600 mx-auto" />
          <p className="text-slate-700">This verification link is invalid or has expired.</p>
          <AuthLink to="/signup">Sign up again</AuthLink>
        </>)}
      </div>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true); toast.success("If the account exists, a reset link was sent.");
      if (data.dev_reset_token) setDevLink(`/reset-password?token=${data.dev_reset_token}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <AuthShell title="Reset password" subtitle="We'll email you a secure reset link" testid="forgot-page">
      {sent ? (
        <div className="space-y-4 text-sm text-slate-600">
          <p>Check your inbox for a password reset link (valid 1 hour).</p>
          {devLink && <a href={devLink} className="text-blue-600 font-medium hover:underline" data-testid="dev-reset-link">Dev: reset now →</a>}
          <div><AuthLink to="/login">Back to sign in</AuthLink></div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className={inputClass} data-testid="forgot-email-input" />
          </Field>
          <button disabled={loading} className={btnClass} data-testid="forgot-submit-button">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Send reset link
          </button>
          <div className="text-sm text-center"><AuthLink to="/login">Back to sign in</AuthLink></div>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const token = params.get("token");
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in."); nav("/login");
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <AuthShell title="Choose a new password" testid="reset-page">
      <form onSubmit={submit} className="space-y-4">
        <Field label="New password">
          <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
            className={inputClass} data-testid="reset-password-input" placeholder="At least 8 characters" />
        </Field>
        <button disabled={loading} className={btnClass} data-testid="reset-submit-button">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Update password
        </button>
      </form>
    </AuthShell>
  );
}

export function AdminRecovery() {
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", recovery_code: "", new_password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/auth/recover-admin", form);
      toast.success("Password reset via recovery. Please sign in."); nav("/login");
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <AuthShell title="Super Admin recovery" subtitle="Use a single-use recovery code to reset access" testid="recovery-page">
      <form onSubmit={submit} className="space-y-4">
        <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl p-3">
          Recovery requires a valid single-use recovery code. All attempts are rate-limited and audited.
        </div>
        <Field label="Super Admin email">
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputClass} data-testid="recovery-email-input" />
        </Field>
        <Field label="Recovery code">
          <input required value={form.recovery_code} onChange={(e) => setForm({ ...form, recovery_code: e.target.value })}
            className={`${inputClass} font-mono`} data-testid="recovery-code-input" />
        </Field>
        <Field label="New password">
          <input type="password" required minLength={8} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            className={inputClass} data-testid="recovery-password-input" />
        </Field>
        <button disabled={loading} className={btnClass} data-testid="recovery-submit-button">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Recover account
        </button>
        <div className="text-sm text-center"><AuthLink to="/login">Back to sign in</AuthLink></div>
      </form>
    </AuthShell>
  );
}
